/**
 * Die Zugangstoken des Mandanten -- F6-01.
 *
 * **Die Liste wird nicht zwischengespeichert.** Sonst gaebe es den Fall, in
 * dem ein Administrator widerruft und die Ansicht den Token weiter als
 * gueltig zeigt -- ausgerechnet an der Stelle, an der jemand wissen will, ob
 * die Sperre gegriffen hat. Eine Handvoll Zeilen kostet die frische Abfrage
 * nichts.
 *
 * **Die vergebbaren Rechte kommen vom Server** und werden nicht im Client aus
 * den eigenen Rechten und einer hinterlegten Sperrliste gebildet. Die
 * Sperrliste steht in `core/permissions.py`; sie hier ein zweites Mal zu
 * fuehren hiesse, dass die zweite Fassung von einer Aenderung der ersten
 * nichts erfaehrt.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { components } from '../../api/schema';
import { request } from '../../lib/apiClient';
import { ApiError } from '../../lib/apiError';

export type ApiTokenRead = components['schemas']['ApiTokenRead'];
export type ApiTokenCreated = components['schemas']['ApiTokenCreated'];
export type GrantablePermissions = components['schemas']['GrantablePermissions'];

export const apiTokenKeys = {
  list: ['api-tokens'] as const,
  grantable: ['api-tokens', 'grantable'] as const,
};

/** Der Zustand einer Zeile -- abgeleitet, nicht gespeichert. */
export type TokenState = 'active' | 'revoked' | 'expired';

export function stateOf(token: ApiTokenRead, now: Date = new Date()): TokenState {
  if (token.revoked_at) return 'revoked';
  if (token.expires_at && new Date(token.expires_at) <= now) return 'expired';
  return 'active';
}

export function useApiTokens() {
  return useQuery({
    queryKey: apiTokenKeys.list,
    queryFn: () => request<ApiTokenRead[]>('/api-tokens'),
    staleTime: 0,
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.isPermanent) && failureCount < 2,
  });
}

/**
 * Was sich weitergeben laesst und was nicht.
 *
 * Die Rechte einer Rolle aendern sich waehrend einer Sitzung selten -- fuenf
 * Minuten sind hier reichlich und ersparen die Abfrage bei jedem Oeffnen des
 * Formulars.
 */
export function useGrantablePermissions() {
  return useQuery({
    queryKey: apiTokenKeys.grantable,
    queryFn: () => request<GrantablePermissions>('/api-tokens/grantable-permissions'),
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.isPermanent) && failureCount < 2,
  });
}

/**
 * Stellt einen Token aus -- F6-01.
 *
 * Die Antwort traegt das Geheimnis. Es steht dort **einmal** und danach
 * nirgends mehr; der Aufrufer zeigt es an und speichert es nicht.
 */
export function useCreateApiToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      name: string;
      description: string;
      permissions: string[];
      expires_at: string | null;
    }) => request<ApiTokenCreated>('/api-tokens', { method: 'POST', body: payload }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: apiTokenKeys.list });
    },
  });
}

export function useRevokeApiToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tokenId: string) =>
      request<ApiTokenRead>(`/api-tokens/${tokenId}/actions/revoke`, { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: apiTokenKeys.list });
    },
  });
}
